/**
 * Gift cards: the list, its filters and the new gift card form.
 * Conventions: see `common.ts`.
 */
const giftCards: Record<string, string> = {
  title: 'Gift cards',
  subtitle: 'Stored-value codes customers can spend at your store.',
  copy_code: 'Copy code',
  create: 'Create gift card',
  creating: 'Creating…',
  exporting: 'Exporting…',
  add_product: 'Add gift card product',
  view_products: 'View gift card products',
  reactivate: 'Reactivate',
  deactivate: 'Deactivate',
  never: 'Never',

  'status.ACTIVE': 'Active',
  'status.REDEEMED': 'Redeemed',
  'status.EXPIRED': 'Expired',
  'status.DISABLED': 'Deactivated',

  'created.prefix': 'Gift card created:',
  'created.worth': 'worth {amount}.',

  'filters.search_label': 'Search gift cards',
  'filters.status_label': 'Filter by status',
  'filters.all_statuses': 'All statuses',

  'list.load_failed': 'Could not load gift cards.',
  'list.empty_filtered_title': 'No gift cards match',
  'list.empty_title': 'No gift cards sold or sent yet',
  'list.empty_filtered_description': 'Try a different code or status.',
  'list.empty_description': 'Gift cards sold or sent to customers will show up here.',
  'list.balance_of': 'of {amount}',

  'columns.code': 'Code',
  'columns.status': 'Status',
  'columns.customer': 'Customer',
  'columns.balance': 'Balance',
  'columns.expires': 'Expires',
  'columns.created': 'Created',

  'select_row': 'Select card {code}',
  'total_cards_one': '{count} gift card',
  'total_cards_other': '{count} gift cards',

  'columns.note': 'Note',

  'filters.search_placeholder': 'Search by code, customer or note',
  'filters.assignment': 'Issued to',
  'filters.assigned': 'A customer',
  'filters.unassigned': 'Nobody yet',
  'filters.issued': 'Issued',
  'filters.expiry': 'Expiry date',
  'filters.expiring_soon': 'Expiring soon',
  'filters.within_days': 'Within {days} days',

  'sort.newest': 'Newest first',
  'sort.oldest': 'Oldest first',
  'sort.balance_desc': 'Highest balance',
  'sort.balance_asc': 'Lowest balance',
  'sort.value_desc': 'Highest value issued',
  'sort.expiry_soonest': 'Expiring soonest',
  'sort.expiry_latest': 'Expiring latest',
  'sort.code_asc': 'Code A–Z',

  'kpi.cards': 'Gift cards',
  'kpi.active': 'Still spendable',
  'kpi.active_hint': 'Not expired, not disabled, with a balance',
  'kpi.outstanding': 'Outstanding',
  'kpi.outstanding_hint': 'Paid for and not yet spent',
  'kpi.redeemed': 'Redeemed',
  'kpi.redeemed_hint': 'Of {amount} issued',

  'detail.spent': 'Spent',
  'detail.disabled_on': 'Deactivated on',
  'detail.history': 'Card history',
  'detail.history_empty': 'Nothing has happened to this card yet.',
  'detail.balance_after': 'Balance {amount}',

  'transaction.ISSUE': 'Issued',
  'transaction.REDEEM': 'Spent at checkout',
  'transaction.REFUND': 'Refunded back',
  'transaction.ADJUSTMENT': 'Manual adjustment',

  'bulk.disable_title': 'Deactivate gift cards',
  'bulk.disable_message_one':
    'Deactivate {count} gift card? Its balance stays, but nobody can spend it.',
  'bulk.disable_message_other':
    'Deactivate {count} gift cards? Their balances stay, but nobody can spend them.',

  'toast.created': 'Gift card {code} created.',
  'toast.deactivated': '{code} deactivated.',
  'toast.reactivated': '{code} is spendable again.',
  'toast.bulk_updated_one': '{count} gift card updated.',
  'toast.bulk_updated_other': '{count} gift cards updated.',

  'export.csv': 'Gift cards (CSV)',
  'export.done': 'Your gift card export is downloading.',

  'form.title': 'New gift card',
  'form.description':
    'The card is issued with its full value as balance. Leave the code blank to have one generated.',
  'form.fields.value': 'Value',
  'form.value_hint': 'What the card is worth, in rupees.',
  'form.fields.code': 'Code',
  'form.code_placeholder': 'Generated automatically',
  'form.code_hint': 'Optional. 8–32 letters and numbers.',
  'form.fields.expires_on': 'Expires on',
  'form.expires_past': 'That date has already passed. Pick today or a later date.',
  'form.expires_hint': 'Optional. Usable through this day.',
  'form.fields.find_customer': 'Find a customer',
  'form.find_customer_placeholder': 'Name or email',
  'form.find_customer_hint': 'Optional. Attach the card to someone.',
  'form.fields.customer': 'Customer',
  'form.no_customer': 'No customer',
  'form.fields.note': 'Note',
  'form.note_placeholder': 'Only your team sees this.',
  'form.create_failed': 'Could not create this gift card.',
};

export default giftCards;
